/**
 * Participant link codes in URLs are logins: whoever holds one answers as that participant. They
 * must not reach error reports or logs (CLAUDE.md; docs/plans/PARTICIPANTS_REDESIGN_PLAN.md, WP13).
 * These helpers replace them, and sign-up codes, before anything leaves the browser.
 */

/** 32 hex characters (uuid4().hex) or a dashed uuid, as participant links use. */
const LINK_CODE = /\b[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}\b/gi;
/** /join/<code>: the public sign-up code. */
const JOIN_CODE = /(\/join\/)[A-Za-z0-9]{4,16}/g;
/**
 * "~" + a JWT: the creator's own preview link (`PREVIEW_TOKEN_PREFIX`, backend/utils/auth.py). It
 * opens the survey as a preview, and its payload names the creator — `sub` is their address — so
 * it is as sensitive as a link code and is not hex, which is why LINK_CODE misses it. Replaced
 * first: a hex run inside the token would otherwise break the token apart and leave the rest.
 */
const PREVIEW_TOKEN = /~eyJ[A-Za-z0-9_-]*(?:\.[A-Za-z0-9_-]+){2}/g;

export function scrubLinkCodes(text: string): string;
export function scrubLinkCodes(text: string | undefined | null): string | undefined | null;
export function scrubLinkCodes(text: string | undefined | null): string | undefined | null {
  if (!text) { return text; }
  return text.replace(PREVIEW_TOKEN, '~<token>').replace(LINK_CODE, '<code>').replace(JOIN_CODE, '$1<code>');
}

/**
 * Sentry's own ids (event, trace, span, replay, the DSN key) are 32 hex characters too. They are
 * not link codes, and replacing them cuts the links between an error, its replay and its trace.
 */
const SENTRY_ID_KEYS = new Set([
  'event_id', 'trace_id', 'span_id', 'parent_span_id', 'replay_id', 'replayId', 'profile_id', 'public_key',
]);

/**
 * Scrub every string in a JSON-like value and return the result. Nothing is written in place: a
 * plain object or array is copied where a string inside it changes, because Sentry hands over
 * frozen and getter-only data, and anything thrown here drops the event. Other objects (the
 * SDK's scopes in sdkProcessingMetadata, the app's live objects in console breadcrumbs) are not
 * walked; Sentry normalizes the event data to plain values before beforeSend sees it.
 */
export function scrubDeep<T>(value: T, depth = 0): T {
  if (depth > 8 || value == null) { return value; }
  if (typeof value === 'string') { return scrubLinkCodes(value) as unknown as T; }
  if (Array.isArray(value)) {
    let copy: unknown[] | undefined;
    value.forEach((item, i) => {
      const next = scrubDeep(item, depth + 1);
      if (next !== item) { (copy ??= [...value])[i] = next; }
    });
    return (copy ?? value) as unknown as T;
  }
  const object: unknown = value;
  if (isPlainObject(object)) {
    let copy: Record<string, unknown> | undefined;
    for (const key of Object.keys(object)) {
      if (SENTRY_ID_KEYS.has(key)) { continue; }
      const item = object[key];
      const next = scrubDeep(item, depth + 1);
      if (next !== item) { (copy ??= { ...object })[key] = next; }
    }
    return (copy ?? value) as T;
  }
  return value;
}

/**
 * Sentry's beforeBreadcrumb. On participant pages the console can hold answers and comment text,
 * which would ride along with the next error as breadcrumbs, so those pages send no console
 * breadcrumbs at all; every other breadcrumb loses its link codes.
 */
export function filterBreadcrumb<T extends { category?: string }>(breadcrumb: T, participantView: boolean): T | null {
  if (participantView && breadcrumb.category === 'console') { return null; }
  return scrubDeep(breadcrumb);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null) { return false; }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
